// @givmo/app-admin — employer gift-card Idempotency-Key signature (EP-WEB4). ★ MONEY-MOVING ★
//
// Sending a company gift card CHARGES a company card or DEBITS the company wallet via
// `POST /admin/company/gift-cards`, guarded by the backend `claim_idempotency` dependency — which
// de-dupes ONLY across retries carrying the SAME `Idempotency-Key` + `purchase_request_id`. An UNKEYED
// retry (or a fresh key on retry) mints a new server-side series and does NOT cross-retry-dedup, so a
// double-click / network retry would DOUBLE-CHARGE. A stable client key is the only thing preventing
// that. (Unlike the /give flow, this key rides BOTH the `Idempotency-Key` header AND the body
// `purchase_request_id` — the gift-card wire carries it in both places; see `buildCompanyGiftCardBody`.)
//
// Series identity for a gift-card send = the exact recipient (work email) + the whole-dollar amount +
// the funding source (a specific card id, or the wallet). Changing ANY of these is a genuinely
// different gift → a fresh key (re-mint on edit). The generic lifecycle holder is shared from
// `@givmo/payments`; this file supplies only the gift-card-specific signature + a bound factory.

import { IdempotencyKeyHolder } from '@givmo/payments';
import type { Funding } from './gift-cards-decode';

/** A stable signature of the gift being sent; a change ends the current attempt-series. */
export interface GiftCardSignature {
  /** The recipient roster employee's work email (the `recipient_work_email` delivery address). */
  readonly recipientWorkEmail: string;
  /** Whole-dollar integer amount. */
  readonly amount: number;
  /** The funding source — a specific card id, or the wallet (encodes the `card_id` axis). */
  readonly funding: Funding;
}

/** Deterministic fingerprint of a gift: recipient + amount + funding instrument (incl. card id). */
export function giftCardFingerprint(signature: GiftCardSignature): string {
  const fundingTag =
    signature.funding.kind === 'wallet' ? 'wallet' : `card:${signature.funding.cardId}`;
  return `email:${signature.recipientWorkEmail}|amount:${signature.amount}|funding:${fundingTag}`;
}

/**
 * Construct the gift-card idempotency-key holder, bound to {@link giftCardFingerprint}. A React
 * component holds ONE holder for its lifetime (a `useRef`), so the key is stable across re-renders by
 * construction and re-mints only when the signature changes. `generate` is injectable for tests. The
 * minted value (a UUIDv4) satisfies the backend `purchase_request_id` min-length-8 constraint.
 */
export function createGiftCardIdempotencyKeyHolder(
  generate?: () => string,
): IdempotencyKeyHolder<GiftCardSignature> {
  return new IdempotencyKeyHolder<GiftCardSignature>(giftCardFingerprint, generate);
}
